import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import GoogleMapReact from 'google-map-react';
import MapsHelpers from '../../helpers/MapsHelpers/MapsHelpers';

/**
 * This component is an abstraction of google-map-react.
 *  - https://github.com/google-map-react/google-map-react
 * It should be used instead of calling the library directly.
 * As per the google maps API, the parent of this container must have height set.
 */
export default class MapView extends React.Component {
  constructor(props) {
    super(props);

    MapView.propTypes = {
      /** 
       * React components to display on the map.
       * Each child should have lat and lng props to specify position.
       */
      children: PropTypes.arrayOf(PropTypes.object),
      /** 
       * Parent components can use this to interface directly with the google maps API.
       * The function will be passed one parameter - {map, maps}
       */
      handleMapsLoaded: PropTypes.func,
      /** 
       * Parent components will be notified when the maps zoom or positioning
       * has changed. The function is passed on parameter which is the center,
       * bounds, size and zoom of the map along with flags to indicate the
       * previous zoom (lastZoom) and whether or not the zoom/positioning
       * was user initiated (userInitiated) versus initiated by this component
       */
      handleOnChange: PropTypes.func,
      /** Passed in as defaultCenter to google maps - {lat, lng} */
      defaultCenter: PropTypes.object,
      /** The center of the map - distinct form defaultCenter in that it can be updated */
      center: PropTypes.object,
      zoom: PropTypes.number,
      /** gMap MapOptions object */
      mapOptions: PropTypes.object,
      /** all other props supported by <GoogleMapReact/> may also be used */
    };

    const defaultMapOptions = {
      streetViewControl: true,
    };
    this.mapOptions = Object.assign(defaultMapOptions, props.mapOptions);
    // default to Golden, CO if no coords passed in
    this.defaultCenter = { lat: 39.7555, lng: -105.2211 };

    this.handleMapsLoaded = this.handleMapsLoaded.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }

  componentDidMount() {
    if (this.map) {
      // This is called here as well as in handleMapsLoaded()
      // because we have no guarantee which will fire first.
      this.fitBoundsOfChildren();
    }
    this.lastOnChange = null;
  }

  componentDidUpdate(prevProps) {
    // TODO: be smarter about checking children? []length change or lat/lng changes?
    if (this.map && (this.props.children != prevProps.children || prevProps.center != this.props.center)){
      this.fitBoundsOfChildren();
    }
    if (this.maps && this.props.idToProtect != prevProps.idToProtect) {
      if (this.props.idToProtect && this.maps.OverlayView) {
        const element = document.getElementById(this.props.idToProtect);
        if (element) {
          this.maps.OverlayView.preventMapHitsAndGesturesFrom(element);
        }
        else {
          this.maps.OverlayView.preventMapHitsAndGesturesFrom(null);
        }
      }
      else {
        this.maps.OverlayView.preventMapHitsAndGesturesFrom(null);
      }
    }
  }

  areCoordinatesValid(lat, lng) {
    return ((lat >= -90 && lat <= 90) && (lng >= -180 && lng <= 180));
  }

  fitBoundsOfChildren() {
    const { center, zoom, children } = this.props;
    if (!((center && center.lat && center.lng) || zoom)) {
      const bounds = new this.maps.LatLngBounds();
      if (children && children.length) { //only add center and zoom if children has length, otherwise use default
        let pointCount = 0;
        if (children.length == 1 && children[0].props.locationsGeoJSON) {
          // child is a cluster containing all pins - extend bounds to fit all pins in cluster
          for (let feature of children[0].props.locationsGeoJSON) {
            const { lat, lng } = MapsHelpers.getLatLngFromGeoJSONFeature(feature);
            if (this.areCoordinatesValid(lat, lng)) {
              pointCount++;
              bounds.extend({ lat, lng });
            }
          }
        }
        for (let component of children) {
          const { lat, lng } = component.props;
          if (this.areCoordinatesValid(lat, lng)) {
            pointCount++;
            bounds.extend({ lat, lng });
          }
        }
        if (pointCount == 1) {
          // using fitBounds on one point zooms to the max level
          this.map.setCenter(bounds.getCenter());
          this.map.setZoom(16);
          this.lastOnChange = null;
        }
        else this.map.fitBounds(bounds);
      }
    }
  }

  handleMapsLoaded(gMapsInterface) {
    this.map = gMapsInterface.map;
    this.maps = gMapsInterface.maps;
    this.fitBoundsOfChildren();

    if (this.props.handleMapsLoaded) {
      this.props.handleMapsLoaded(gMapsInterface);
    }
  }

  /** 
   * Fired whenever the map view changes, returns a marginBounds object
   * We add a few custom properties to this object.
   *   1. userInitiated (bool)
   *   2. lastOnChange - marginBounds object from the last time the view changed
   *   3. boundsFormatted - map bounds in the format of [westLng, southLat, eastLng, northLat]
   */
  handleOnChange(marginBounds) {
    if (this.props.handleMapPan) {
      this.props.handleMapPan(false);
    }
    const marginBoundsCopy = _.cloneDeep(marginBounds);
    marginBoundsCopy.userInitiated = this.lastOnChange ? true : false;
    marginBoundsCopy.lastOnChange = _.cloneDeep(this.lastOnChange);
    this.lastOnChange = _.cloneDeep(marginBounds);
    const { ne, sw } = marginBounds.bounds;
    marginBoundsCopy.boundsFormatted = [sw.lng, sw.lat, ne.lng, ne.lat];
    if (this.props.handleOnChange) {
      this.props.handleOnChange(marginBoundsCopy);
    }
  }

  handleDrag(args) {
    if (this.props.handleMapPan) {
      this.props.handleMapPan(true);
    }
  }

  handleMouseUp(childKey, childProps, mouse) {
    if (this.props.handleMapPan) {
      this.props.handleMapPan(false);
    }
  }

  render() {
    const propsForGoogle = Object.assign({}, this.props);
    // remove props we don't want to pass to google
    delete propsForGoogle.children;
    delete propsForGoogle.defaultCenter;
    delete propsForGoogle.handleMapsLoaded;

    const googleMap =  (
      <GoogleMapReact
        bootstrapURLKeys={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        defaultZoom={11}
        draggable={true}
        defaultCenter={this.props.defaultCenter || this.defaultCenter}
        center={this.props.center}
        zoom={this.props.zoom}
        onGoogleApiLoaded={this.handleMapsLoaded}
        options={this.mapOptions}
        yesIWantToUseGoogleMapApiInternals={true}
        onClick={this.props.handleClick}
        onChange={this.handleOnChange}
        onDrag={this.handleDrag}
        {...propsForGoogle}
      >
        {this.props.children}
      </GoogleMapReact>
    );
    
    return googleMap;
  }
}



// WEBPACK FOOTER //
// ./src/components/MapView/index.js